import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Check, CalendarClock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// Cancelación del plan: muestra el plan, hasta cuándo sigue vigente, qué se pierde y qué se conserva
// antes de confirmar. Con el plan al día se cancela al final del periodo pagado (se puede deshacer).
export function CancelPlanDialog({ open, onOpenChange, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; onDone: () => void }) {
  const [info, setInfo] = useState<any>(null);
  const [failed, setFailed] = useState("");
  const [reason, setReason] = useState<string>("");
  const [reasonText, setReasonText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInfo(null); setFailed(""); setReason(""); setReasonText("");
    api.get("/payments/cancel-preview").then((r) => setInfo(r.data)).catch((e) => setFailed(apiError(e)));
  }, [open]);

  const immediate = info?.mode === "inmediata";

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/cancel", { reason_code: reason || null, reason_text: reasonText.trim() || null });
      toast.success(immediate
        ? `Tu plan ${data.plan_name} quedó cancelado. Te enviamos la confirmación por correo.`
        : `Listo: tu plan seguirá activo hasta el ${data.effective_label} y no se renovará. Te enviamos la confirmación por correo.`);
      onOpenChange(false);
      onDone();
    } catch (e: any) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900 sm:max-w-xl" data-testid="cancel-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cancelar {info?.plan_name || "tu plan"}</DialogTitle>
          <DialogDescription className="text-slate-600">Revisa lo que cambia antes de confirmar.</DialogDescription>
        </DialogHeader>

        {failed ? (
          <p className="text-sm text-red-700" role="alert">{failed}</p>
        ) : !info ? (
          <div className="flex justify-center py-8" role="status"><Loader2 className="w-6 h-6 animate-spin text-navy" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>
        ) : (
          <div className="space-y-5">
            <div className={`p-4 rounded-xl border text-sm flex items-start gap-3 ${immediate ? "bg-red-500/10 border-red-500/30 text-red-900" : "bg-navy/5 border-navy/20 text-slate-800"}`} data-testid="cancel-effective">
              <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" aria-hidden="true" />
              {immediate ? (
                <p>Tu plan tiene un <strong>pago pendiente</strong>, así que se cancela <strong>hoy</strong>: dejas de tener acceso a sus funciones y no se intentará cobrarte de nuevo.</p>
              ) : (
                <p>Seguirás teniendo <strong>{info.plan_name}</strong> hasta el <strong>{info.effective_label}</strong>, el final del periodo que ya pagaste. <strong>No se te volverá a cobrar.</strong> Hasta esa fecha puedes cambiar de opinión y mantenerlo.</p>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">{immediate ? "Dejas de tener acceso a:" : `Desde el ${info.effective_label} dejarás de tener:`}</h3>
              <ul className="space-y-1.5" data-testid="cancel-losses">
                {info.losses.map((f: string) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><X className="w-4 h-4 text-red-600 mt-0.5 shrink-0" aria-hidden="true" />{f}</li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Conservas, sin costo, Armony Contigo:</h3>
              <ul className="space-y-1.5">
                <li className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" aria-hidden="true" />Tus {info.points.toLocaleString("es-EC")} puntos y tu nivel {info.tier}</li>
                {info.keeps.slice(1).map((f: string) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" aria-hidden="true" />{f}</li>
                ))}
              </ul>
            </div>

            <fieldset>
              <legend className="text-sm font-semibold text-slate-800 mb-2">¿Por qué cancelas? (opcional)</legend>
              <div className="grid sm:grid-cols-2 gap-2">
                {info.reasons.map((r: any) => (
                  <label key={r.code} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm ${reason === r.code ? "border-navy bg-navy/5" : "border-slate-200 hover:bg-slate-50"}`}>
                    <input type="radio" name="cancel-reason" value={r.code} checked={reason === r.code} onChange={() => setReason(r.code)} className="accent-navy" />
                    {r.label}
                  </label>
                ))}
              </div>
              <Label htmlFor="cancel-reason-text" className="sr-only">Cuéntanos más</Label>
              <Textarea id="cancel-reason-text" data-testid="cancel-reason-text" rows={2} maxLength={500} value={reasonText} onChange={(e: any) => setReasonText(e.target.value)}
                placeholder="Cuéntanos más, si quieres. Nos ayuda a mejorar." className="mt-2 bg-obsidian border-slate-200" />
            </fieldset>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-slate-200 text-slate-700 rounded-full">
            Mantener mi plan
          </Button>
          <Button data-testid="confirm-cancel-plan" onClick={confirm} disabled={busy || !info} className="bg-red-700 hover:bg-red-800 text-white font-semibold rounded-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : "Confirmar cancelación"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
